import { Link, useParams } from 'react-router-dom';
import { getReservation, reservationMarkPaid, reservationMarkPending, reservationVoid } from '@/lib/api';
import { useApiGet, useApiSend } from '@/lib/api/client';
import Layout from './layout';
import Money from '@/ui/money';
import Loading from '@/components/loading';
import { Switch } from '@/ui/switch';
import { Label } from '@/ui/label';
import { ElementType, useState } from 'react';
import { Button } from '@/ui/button';
import { TicketIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Reservations } from '@/global';
import {
  formatDate,
  formatDateTime,
  getPropertyValueOrReference,
} from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Heading } from '@/ui/heading';
import { ReservationStatusBadge } from '@/components/shared/reservation-status-badge';
import { StatusBadge } from '@/ui/status-badge';
import {
  calculateBalance,
  calculateDeposit,
  calculateFee,
  calculateTotal,
  calculateValue,
  isRecentSellerReservation,
  sortedVariantsByReservation,
} from '@/lib/reservation';
import {
  BanknoteIcon,
  CheckCircleIcon,
  ClockIcon,
  Coins,
  CreditCard,
  Euro,
  Landmark,
  PiggyBankIcon,
  Receipt,
  ReceiptIcon,
  Theater,
} from 'lucide-react';
import { Badge } from '@/ui/badge';
import store from '../store';
import ThermalTicket from './thermal-ticket';

function Reservation() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = store();
  const u = user?.data;

  const [showCosts, setShowCosts] = useState(false);

  const {
    data: reservationData,
    isLoading: isLoadingGetReservation,
    isFetching: isFetchingGetReservation,
    refetch,
  } = useApiGet([], async () => await getReservation({ id }), {
    enabled: true,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const { isPending: isVoidPending, mutate: voidMutation } = useApiSend(
    (data: Reservations) => {
      return reservationVoid(data.id);
    },
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const { isPending: isMarkPendingPending, mutate: markPendingMutation } = useApiSend(
    (data: Reservations) => reservationMarkPending(data.id),
    { onSuccess: () => { refetch(); } }
  );

  const { isPending: isMarkPaidPending, mutate: markPaidMutation } = useApiSend(
    (data: Reservations) => reservationMarkPaid(data.id),
    { onSuccess: () => { refetch(); } }
  );

  const isLoading =
    isLoadingGetReservation || isFetchingGetReservation || isVoidPending || isMarkPendingPending || isMarkPaidPending;

  if (!reservationData) {
    return;
  }

  return (
    <Layout
      side={
        !isLoading && (
          <div className="flex flex-col w-full p-4 space-y-3 text-left">
            <Button asChild variant="outline" size="lg">
              <Link to={`/ticket/${id}`} target="_blank">
                <TicketIcon className="mr-2 size-4" />
                {t('reservation.print-ticket')}
              </Link>
            </Button>

            <ThermalTicket reservation={reservationData} />

            {reservationData?.status === 'confirmed' && (
              <Button
                variant="destructive"
                disabled={isVoidPending}
                size="lg"
                onClick={() => {
                  voidMutation(reservationData);
                }}
              >
                <XMarkIcon className="mr-2 size-4" />
                {t('reservation.void')}
              </Button>
            )}

            {reservationData?.status === 'confirmed' &&
              (!reservationData.payment_status || reservationData.payment_status === 'none' || reservationData.payment_status === 'paid') && (
              <Button
                variant="outline"
                disabled={isMarkPendingPending}
                size="lg"
                onClick={() => {
                  markPendingMutation(reservationData);
                }}
              >
                <ClockIcon className="mr-2 size-4" />
                {t('reservation.mark-pending')}
              </Button>
            )}

            {reservationData?.status === 'confirmed' &&
              reservationData.payment_status === 'pending' && (
              <Button
                variant="default"
                disabled={isMarkPaidPending}
                size="lg"
                onClick={() => {
                  markPaidMutation(reservationData);
                }}
              >
                <CheckCircleIcon className="mr-2 size-4" />
                {t('reservation.mark-paid')}
              </Button>
            )}
          </div>
        )
      }
    >
      {!isLoading ? (
        <>
          <Heading
            className="p-4"
            title={
              <span className="capitalize">
                {reservationData?.customer_name}
              </span>
            }
            description={`${reservationData?.customer_phone} - ${reservationData?.customer_email}`}
          />

          <div className="w-full p-4 space-y-4">
            <dl className="divide-y divide-gray-100">
              <Row
                title={t('reservation.service.title')}
                value={reservationData.service.name}
              />
              <Row
                title={t('reservation.booked-date.title')}
                value={formatDate(reservationData.booked_date)}
              />
              <Row
                title={t('reservation.shift.title')}
                value={`${reservationData.shift.name}: ${reservationData.shift.from} → ${reservationData.shift.to}`}
              />
              <Row
                title={t('reservation.status.title')}
                value={<ReservationStatusBadge status={reservationData.status} />}
              />
              <Row
                title={t('reservation.variants.title')}
                value={
                  <ul className="w-full border border-gray-200 divide-y divide-gray-100 rounded-md">
                    {sortedVariantsByReservation(reservationData)?.map(
                      (variant) => (
                        <li
                          key={variant.id}
                          className="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6"
                        >
                          <div className="flex items-center flex-1 w-0">
                            <TicketIcon className="text-gray-400 size-5 shrink-0" />
                            <div className="flex flex-1 min-w-0 gap-2 ml-4">
                              {variant.amount} &times;{' '}
                              {getPropertyValueOrReference(
                                variant.variant,
                                'name'
                              )}
                            </div>
                          </div>
                          <div className="flex ml-4 space-x-4 shrink-0">
                            <Money amount={variant.total} />
                          </div>
                        </li>
                      )
                    )}
                  </ul>
                }
              />
              <Row
                title={<>{t('reservation.totals.title')} </>}
                value={
                  <div className="w-full space-y-4">
                    <ul className="w-full border border-gray-200 divide-y divide-gray-100 rounded-md">
                      <TotalRow
                        title={
                          <>
                            {t('reservation.totals.deposit')}{' '}
                            {isRecentSellerReservation(
                              reservationData,
                              u.id
                            ) ? (
                              <Badge
                                variant="outline"
                                className="text-orange-400 animate-pulse"
                              >
                                {t('reservation.totals.recall-to-take-deposit')}
                              </Badge>
                            ) : null}
                          </>
                        }
                        value={calculateDeposit(reservationData)}
                        icon={Coins}
                      />
                      <TotalRow
                        title={t('reservation.totals.balance')}
                        value={calculateBalance(reservationData)}
                        icon={BanknoteIcon}
                      />
                    </ul>
                    <Label className="flex items-center gap-2">
                      <Switch
                        checked={showCosts}
                        onCheckedChange={setShowCosts}
                      />
                      {t('reservation.totals.show-fees')}
                    </Label>
                    {showCosts && (
                      <ul className="w-full border border-gray-200 divide-y divide-gray-100 rounded-md">
                        <TotalRow
                          title={t('reservation.totals.fee')}
                          value={calculateFee(reservationData)}
                          icon={PiggyBankIcon}
                        />
                        <TotalRow
                          title={t('reservation.totals.value')}
                          value={calculateValue(reservationData)}
                          icon={Landmark}
                        />
                      </ul>
                    )}
                    <ul className="w-full border border-gray-200 divide-y divide-gray-100 rounded-md">
                      <TotalRow
                        title={t('reservation.totals.total')}
                        value={calculateTotal(reservationData)}
                        icon={Euro}
                      />
                    </ul>
                  </div>
                }
              />
              <Row
                title={t('reservation.seller.title')}
                value={
                  <div className="flex flex-col gap-1">
                    <div>
                      {reservationData.seller.first_name}{' '}
                      {reservationData.seller.last_name} •{' '}
                      {reservationData.seller.company}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDateTime(reservationData.date_created)}
                    </div>
                  </div>
                }
              />
              {reservationData.payment_status && reservationData.payment_status !== 'none' && (
                <Row
                  title={
                    <div className="flex items-center gap-2">
                      <CreditCard className="text-gray-400 size-4" />
                      {t('reservation.payment.title')}
                    </div>
                  }
                  value={
                    <div className="w-full space-y-2">
                      <div className="flex items-center gap-2">
                        <PaymentStatusBadge status={reservationData.payment_status} />
                      </div>
                      {reservationData.paid_amount != null && (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <span className="text-gray-500">{t('reservation.payment.amount')}:</span>
                          <Money amount={reservationData.paid_amount} />
                        </div>
                      )}
                      {reservationData.paid_at && (
                        <div className="text-sm text-gray-700">
                          <span className="text-gray-500">{t('reservation.payment.date')}:</span>{' '}
                          {formatDateTime(reservationData.paid_at)}
                        </div>
                      )}
                      {reservationData.payment_intent_id && (
                        <div className="text-sm text-gray-700">
                          <span className="text-gray-500">Stripe:</span>{' '}
                          <a
                            href={`https://dashboard.stripe.com/payments/${reservationData.payment_intent_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-blue-600 hover:underline"
                          >
                            {reservationData.payment_intent_id}
                          </a>
                        </div>
                      )}
                      {reservationData.channel && (
                        <div className="text-sm text-gray-700">
                          <span className="text-gray-500">{t('reservation.payment.channel')}:</span>{' '}
                          {t(`reservation.payment.channel-${reservationData.channel}`)}
                        </div>
                      )}
                    </div>
                  }
                />
              )}
            </dl>
          </div>
        </>
      ) : (
        <Loading />
      )}
    </Layout>
  );
}

export default Reservation;

function PaymentStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const config: Record<string, 'emerald' | 'red' | 'amber' | 'blue' | 'gray'> = {
    paid: 'emerald',
    pending: 'amber',
    partial: 'amber',
    refunded: 'blue',
    none: 'gray',
  };
  const color = config[status] ?? 'gray';
  return <StatusBadge color={color} label={t(`reservation.payment.${status}`)} />;
}

function Row({
  title,
  value,
}: {
  title: string | React.ReactNode;
  value: string | React.ReactNode;
}) {
  return (
    <div className="py-2 sm:grid lg:grid-cols-3 sm:gap-4 sm:px-0">
      <dt className="font-medium text-gray-900 text-sm/6">{title}</dt>
      <dd className="flex col-span-2 mt-1 text-gray-700 text-sm/6 sm:mt-0">
        {value}
      </dd>
    </div>
  );
}

function TotalRow({
  title,
  value,
  icon,
}: {
  title: string | React.ReactNode;
  value: number;
  icon: ElementType;
}) {
  const Icon = icon;

  return (
    <li className="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6">
      <div className="flex items-center flex-1 w-0">
        <Icon className="text-gray-400 size-5 shrink-0" />
        <div className="flex flex-1 min-w-0 gap-2 ml-4">{title}</div>
      </div>
      <div className="flex ml-4 space-x-4 shrink-0">
        <Money amount={value} />
      </div>
    </li>
  );
}
